import { atr, closes, ema, rsi, sma } from "./indicators";
import { riskReward, targetFromR } from "./risk";
export const V1_SYMBOLS = ["AMD", "NVDA", "TSLA", "AAPL", "MSFT", "SPY", "QQQ"];
export const strategies = [
    {
        id: "trend-pullback",
        name: "Trend Pullback",
        assetTypes: ["stock", "ETF"],
        minimumBars: 220,
        enabled: true,
        version: "v1",
        description: "Daily long-only pullback: price above SMA200, SMA50 above SMA200, recovery above EMA20, ATR/swing stop."
    },
    {
        id: "breakout-retest",
        name: "Breakout Retest",
        assetTypes: ["stock", "ETF"],
        minimumBars: 120,
        enabled: true,
        version: "v1",
        description: "Daily long-only resistance breakout followed by a controlled retest."
    },
    {
        id: "mean-reversion",
        name: "Mean Reversion",
        assetTypes: ["stock", "ETF"],
        minimumBars: 80,
        enabled: false,
        version: "v1.5+",
        description: "Disabled v1.5+ stub. RSI/Bollinger ranging-market logic is deferred."
    },
    {
        id: "multi-timeframe",
        name: "Multi-timeframe Confluence",
        assetTypes: ["stock", "ETF"],
        minimumBars: 120,
        enabled: false,
        version: "v1.5+",
        description: "Disabled v1.5+ stub. Daily/lower-timeframe confluence is deferred."
    },
    {
        id: "forex-session-breakout",
        name: "Forex Session Breakout",
        assetTypes: ["forex", "metal"],
        minimumBars: 80,
        enabled: false,
        version: "v1.5+",
        description: "Disabled v1.5+ stub. Forex/metals are modeled but not active in v1."
    }
];
export function generateSignal(input) {
    const signal = scoreStrategySetup(input);
    if (!signal || signal.confidence < 80)
        return null;
    return signal;
}
export function scoreStrategySetup(input) {
    const strategy = strategies.find((item) => item.id === input.strategyId);
    if (!strategy ||
        !strategy.enabled ||
        !strategy.assetTypes.includes(input.assetType) ||
        input.candles.length < strategy.minimumBars ||
        !["stock", "ETF"].includes(input.assetType)) {
        return null;
    }
    if (strategy.id === "trend-pullback")
        return trendPullbackSignal(input, strategy);
    if (strategy.id === "breakout-retest")
        return breakoutRetestSignal(input, strategy);
    return null;
}
function trendPullbackSignal(input, strategy) {
    const candles = input.candles;
    const values = closes(candles);
    const last = candles.at(-1);
    const previous = candles.at(-2);
    const sma200 = sma(values, 200);
    const sma50 = sma(values, 50);
    const ema20 = ema(values, 20);
    const rsi14 = rsi(values, 14);
    const atr14 = atr(candles, 14);
    const trendAllowed = last.c > finiteOr(sma200.at(-1), Infinity) && finiteOr(sma50.at(-1), 0) > finiteOr(sma200.at(-1), Infinity);
    const touchedEma20 = previous.l <= finiteOr(ema20.at(-2), previous.c) * 1.03;
    const greenRecovery = last.c > last.o && last.c > finiteOr(ema20.at(-1), last.c);
    const rsiDip = Math.min(...rsi14.slice(-8).filter(Number.isFinite)) <= 45;
    const rsiTurnUp = finiteOr(rsi14.at(-1), 50) >= finiteOr(rsi14.at(-2), 50);
    if (!trendAllowed || !touchedEma20 || !greenRecovery)
        return null;
    const entryTrigger = roundPrice(Math.max(last.h, last.c));
    const latestAtr = finiteOr(atr14.at(-1), last.c * 0.02);
    const stopLoss = roundPrice(selectLongStop(candles, entryTrigger, latestAtr));
    const target1 = roundPrice(targetFromR(entryTrigger, stopLoss, "LONG", 2));
    const target2 = roundPrice(targetFromR(entryTrigger, stopLoss, "LONG", 4));
    const confidence = Math.min(96, 80 + (rsiDip ? 5 : 0) + (rsiTurnUp ? 4 : 0) + trendStrength(values));
    return makeSignal({
        input,
        strategy,
        direction: "LONG",
        entryTrigger,
        stopLoss,
        target1,
        target2,
        confidence,
        explanation: "Daily uptrend filter passed: price is above SMA200, SMA50 is above SMA200, price pulled toward EMA20, and a green closed daily candle recovered above EMA20.",
        invalidatedIf: `Daily close below EMA20 or below stop ${stopLoss}`
    });
}
function breakoutRetestSignal(input, strategy) {
    const candles = input.candles;
    const values = closes(candles);
    const last = candles.at(-1);
    const previous = candles.at(-2);
    const sma200 = sma(values, 200);
    if (Number.isFinite(sma200.at(-1)) && last.c < sma200.at(-1))
        return null;
    const range = candles.slice(-65, -3);
    const resistance = Math.max(...range.map((candle) => candle.h));
    const priorBreak = previous.c > resistance;
    const retestHeld = last.l <= resistance * 1.015 && last.c > resistance && last.c > last.o;
    if (!priorBreak || !retestHeld)
        return null;
    const latestAtr = finiteOr(atr(candles, 14).at(-1), last.c * 0.02);
    const entryTrigger = roundPrice(Math.max(last.h, last.c));
    const stopLoss = roundPrice(Math.min(resistance * 0.99, entryTrigger - latestAtr * 1.5));
    const target1 = roundPrice(targetFromR(entryTrigger, stopLoss, "LONG", 2));
    const target2 = roundPrice(targetFromR(entryTrigger, stopLoss, "LONG", 3));
    const volumeBoost = last.v && previous.v && last.v > previous.v ? 4 : 0;
    return makeSignal({
        input,
        strategy,
        direction: "LONG",
        entryTrigger,
        stopLoss,
        target1,
        target2,
        confidence: Math.min(92, 80 + volumeBoost + trendStrength(values)),
        explanation: "Resistance broke on the prior daily bar and the latest closed daily bar retested that level without failing.",
        invalidatedIf: `Daily close back below breakout level ${roundPrice(resistance)}`
    });
}
function makeSignal({ input, strategy, direction, entryTrigger, stopLoss, target1, target2, confidence, explanation, invalidatedIf }) {
    const last = input.candles.at(-1);
    return {
        id: `${input.symbol}-${strategy.id}-${last.t}`,
        symbol: input.symbol,
        assetType: input.assetType,
        direction,
        strategyId: strategy.id,
        strategyName: strategy.name,
        entry: entryTrigger,
        entryTrigger,
        stopLoss,
        target1,
        target2,
        riskReward: riskReward(entryTrigger, stopLoss, target2, direction),
        confidence: Math.round(confidence),
        explanation,
        dataSource: input.dataMeta?.source ?? input.dataSource ?? "mock/local",
        dataTimestamp: input.dataMeta?.asOf ?? last.t,
        dataMeta: input.dataMeta,
        freshness: input.freshness ?? (input.dataMeta?.isDelayed ? "delayed" : "historical"),
        invalidatedIf,
        status: "PLANNED",
        createdAt: new Date().toISOString()
    };
}
function selectLongStop(candles, entry, latestAtr) {
    const swingLow = Math.min(...candles.slice(-12).map((candle) => candle.l)) * 0.995;
    const atrStop = entry - latestAtr * 2;
    if (entry - swingLow <= latestAtr * 3)
        return swingLow;
    return atrStop;
}
function trendStrength(values) {
    const fast = ema(values, 50).at(-1) ?? 0;
    const slow = sma(values, 200).at(-1) ?? 0;
    const last = values.at(-1) ?? 1;
    if (!Number.isFinite(fast) || !Number.isFinite(slow))
        return 0;
    return Math.min(7, Math.round((Math.abs(fast - slow) / last) * 120));
}
function finiteOr(value, fallback) {
    return Number.isFinite(value) ? Number(value) : fallback;
}
function roundPrice(value) {
    return Number(value.toFixed(value >= 100 ? 2 : 4));
}
